import { Loader2 } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";

export function ConfirmationDialog({
  open,
  isPending,
  onClose,
  onConfirm,
  title,
  heading,
  pendingText = "처리 중...",
  confirmText = "확인",
  cancelText = "취소",
}: {
  readonly open: boolean;
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly title: string;
  readonly heading: string;
  readonly pendingText?: string;
  readonly confirmText?: string;
  readonly cancelText?: string;
}) {
  const returnFocusRef = useRef<HTMLElement | null>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isPending) onClose();
      }}
    >
      <DialogContent
        onOpenAutoFocus={() => {
          returnFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocusRef.current?.isConnected) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
        overlayClassName="bg-black/45"
        hideCloseButton={isPending}
        className="w-[440px] max-w-[calc(100vw-32px)] gap-0 overflow-hidden rounded-[14px] border-0 bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
      >
        <DialogHeader className="px-6 py-5 text-left">
          <DialogTitle className="font-pretendard! text-[18px] font-semibold text-zinc-900">
            {heading}
          </DialogTitle>
          <DialogDescription className="font-pretendard! text-[13px] leading-5 text-zinc-500">
            {title}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 border-t border-zinc-200 px-6 py-4 sm:space-x-0">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            disabled={isPending}
            onClick={onClose}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            className="rounded-full bg-sig text-white hover:bg-sig/90"
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending && (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            )}
            {isPending ? pendingText : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
